<template>
  <div class="sample-request-detail" v-loading="loading">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <el-button @click="goBack" :icon="ArrowLeft" circle />
        <h2>{{ sampleRequest?.request_number || '샘플요청 상세' }}</h2>
        <el-tag v-if="sampleRequest" :type="getStatusType(sampleRequest.status)" size="large">
          {{ sampleRequest.status_display }}
        </el-tag>
        <el-tag
          v-if="sampleRequest && sampleRequest.current_round > 0"
          type="info"
          size="large"
        >
          {{ sampleRequest.current_round }}차 진행
        </el-tag>
      </div>
      <div class="header-actions">
        <el-button
          v-if="canCreateQuotation"
          type="success"
          plain
          @click="goCreateQuotation"
        >이 샘플로 견적 작성</el-button>
        <el-button
          v-if="canEdit"
          type="primary"
          @click="openEditDialog"
        >
          <el-icon><Edit /></el-icon>
          수정
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 정보 패널 — 이미지 + 전체 요청 정보를 한 카드에 (밀도 우선) -->
    <el-card v-if="sampleRequest" class="info-panel" shadow="never">
      <div class="panel-row">
        <div v-if="sampleRequest.design_front_image" class="panel-image">
          <el-image
            :src="sampleRequest.design_front_image"
            fit="contain"
            style="width: 110px; height: 110px;"
            :preview-src-list="[sampleRequest.design_front_image]"
            preview-teleported
          >
            <template #error>
              <div class="image-placeholder">이미지 없음</div>
            </template>
          </el-image>
        </div>
        <div class="panel-grid">
          <div class="info-item"><span class="k">브랜드</span><span class="v">{{ sampleRequest.brand_name }}</span></div>
          <div class="info-item"><span class="k">디자인</span><span class="v">{{ sampleRequest.design_name }}</span></div>
          <div class="info-item"><span class="k">샘플유형</span><span class="v">{{ sampleRequest.sample_type_display }}</span></div>
          <div class="info-item"><span class="k">수량</span><span class="v">{{ sampleRequest.quantity }}</span></div>
          <div class="info-item">
            <span class="k">납기일</span>
            <span class="v" :class="{ 'text-danger': sampleRequest.is_overdue }">
              {{ sampleRequest.due_date }}
              <el-tag
                v-if="sampleRequest.status !== 'COMPLETED' && sampleRequest.status !== 'REJECTED'"
                :type="sampleRequest.is_overdue ? 'danger' : sampleRequest.days_remaining <= 3 ? 'warning' : 'info'"
                size="small"
              >{{ sampleRequest.is_overdue ? 'D+' + Math.abs(sampleRequest.days_remaining) : 'D-' + sampleRequest.days_remaining }}</el-tag>
            </span>
          </div>
          <div class="info-item">
            <span class="k">{{ sampleRequest.status === 'COMPLETED' ? '실제비용' : '예상비용' }}</span>
            <span class="v strong">{{ formatCurrency(sampleRequest.status === 'COMPLETED' ? sampleRequest.actual_cost : sampleRequest.estimated_cost) }}</span>
          </div>
          <div class="info-item"><span class="k">색상</span><span class="v">{{ sampleRequest.colors || '-' }}</span></div>
          <div class="info-item"><span class="k">사이즈</span><span class="v">{{ sampleRequest.sizes || '-' }}</span></div>
          <div class="info-item"><span class="k">요청일</span><span class="v">{{ sampleRequest.request_date }}</span></div>
          <div class="info-item"><span class="k">요청자</span><span class="v">{{ sampleRequest.requester_name || '-' }}</span></div>
          <div class="info-item"><span class="k">담당자(자사)</span><span class="v">{{ sampleRequest.designer_name || '-' }}</span></div>
          <div class="info-item"><span class="k">현재 회차</span><span class="v">{{ sampleRequest.current_round ? sampleRequest.current_round + '차' : '-' }}</span></div>
        </div>
      </div>
      <div v-if="sampleRequest.memo" class="info-memo"><span class="k">메모</span><span class="v">{{ sampleRequest.memo }}</span></div>
    </el-card>

    <!-- 내 차례 결재 배너 — 결재 진행중일 때 (기안자·상신일 흡수, 상태 카드 중복 제거) -->
    <el-card
      v-if="sampleRequest && unifiedApprovalRequest && unifiedApprovalRequest.status === 'PENDING'"
      class="approval-card my-turn-banner"
      shadow="never"
    >
      <div class="banner-row">
        <span class="banner-text">
          🖋 결재 진행중<template v-if="currentApproverName"> · 현재 결재자: <b>{{ currentApproverName }}</b></template>
          <span class="banner-sub">
            기안 {{ unifiedApprovalRequest.requester_name }} · {{ (unifiedApprovalRequest.submitted_at || '').substring(0, 10) }}
          </span>
        </span>
        <ApprovalActionButtons
          :approval-request="unifiedApprovalRequest"
          @refresh="fetchData"
        />
      </div>
    </el-card>

    <!-- 통합 결재 상태 카드 — 진행중이 아닐 때만 (배너와 중복 방지) -->
    <ApprovalStatusCard
      v-if="sampleRequest && unifiedApprovalRequest && unifiedApprovalRequest.status !== 'PENDING'"
      :approval-request="unifiedApprovalRequest"
      class="approval-card"
    />

    <!-- 승인/작업 액션 영역 — 결재(주 경로: 통합결재) / 작업진행 그룹 분리 -->
    <el-card v-if="sampleRequest && showLegacyApprovalActions" class="approval-card">
      <div class="approval-actions">
        <!-- 결재 처리 -->
        <template v-if="showApprovalGroup">
          <span class="approval-label">결재:</span>
          <!-- 주 경로: 통합결재 상신 -->
          <el-button
            v-if="canSubmitApproval"
            type="warning"
            @click="showSubmitDialog = true"
          >
            결재 상신
          </el-button>
          <!-- fallback: 결재선 미설정으로 상신 불가할 때만 직접 승인 -->
          <template v-if="showLegacyDirectApproval">
            <el-button type="success" @click="handleApprove('dept', true)">부서장 승인</el-button>
            <el-button type="danger" @click="handleApprove('dept', false)">반려</el-button>
          </template>
          <template v-if="sampleRequest.status === 'DEPT_APPROVED' && userStore.isSampleApprover">
            <el-button type="success" @click="handleApprove('final', true)">최종 승인</el-button>
            <el-button type="danger" @click="handleApprove('final', false)">반려</el-button>
          </template>
          <span v-if="showLegacyDirectApproval" class="approval-hint">
            결재선 미설정 — 직접 승인 (자금 설정에서 결재선 등록 시 상신 방식 권장)
          </span>
        </template>

        <!-- 작업 진행 -->
        <template v-if="showWorkGroup">
          <span class="approval-label work-label">작업:</span>
          <el-button
            v-if="sampleRequest.status === 'FINAL_APPROVED' && userStore.isSampleManager"
            type="primary"
            @click="handleStartWork"
          >
            작업 시작
          </el-button>
          <el-button
            v-if="sampleRequest.status === 'IN_PROGRESS' && userStore.isSampleManager && !hasRounds"
            type="success"
            @click="handleComplete"
          >
            완료 처리
          </el-button>
        </template>
      </div>
    </el-card>

    <!-- 탭 영역 -->
    <el-tabs v-model="activeTab" class="detail-tabs">
      <!-- 승인 Flow 탭 -->
      <el-tab-pane label="승인 Flow" name="approval-flow">
        <ApprovalStepsTimeline
          :approval-request="unifiedApprovalRequest"
          :legacy-steps="sampleRequest?.approval_steps || []"
          document-api-path="sample-requests"
          :document-id="sampleRequestId"
          :show-actions="true"
          @refresh="fetchData"
        />
      </el-tab-pane>

      <!-- 회차 관리 탭 -->
      <el-tab-pane label="회차 관리" name="rounds">
        <SampleRoundTimeline
          :rounds="sampleRequest?.rounds || []"
          :sample-request="sampleRequest"
          :loading="loading"
          @refresh="fetchData"
          @select-round="handleSelectRound"
        />
        <SampleRoundDetail
          v-if="selectedRound"
          :round="selectedRound"
          :sample-request-id="sampleRequestId"
          @refresh="fetchData"
        />
      </el-tab-pane>

      <!-- 작업 내역 탭 (전체/레거시) -->
      <el-tab-pane label="작업 내역" name="works">
        <el-card>
          <div class="tab-header">
            <span>작업 목록</span>
            <el-button
              v-if="userStore.isSampleManager && canAddWorkLegacy"
              type="primary"
              size="small"
              @click="openWorkDialog"
            >
              작업 추가
            </el-button>
          </div>
          <el-table :data="sampleRequest?.works || []" stripe>
            <el-table-column prop="sequence" label="순서" width="60" align="center" />
            <el-table-column prop="work_type_display" label="작업유형" width="120" />
            <el-table-column label="회차" width="80" align="center">
              <template #default="{ row }">
                {{ row.round_number ? row.round_number + '차' : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="worker_name" label="담당자" width="100" />
            <el-table-column prop="supplier_name" label="외주업체" width="120" />
            <el-table-column label="상태" width="100">
              <template #default="{ row }">
                <el-tag :type="getWorkStatusType(row.status)">
                  {{ row.status_display }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="planned_start" label="예정시작" width="110" />
            <el-table-column prop="planned_end" label="예정종료" width="110" />
            <el-table-column label="비용" width="120" align="right">
              <template #default="{ row }">
                {{ formatCurrency(row.total_cost) }}
              </template>
            </el-table-column>
            <el-table-column label="작업" width="220">
              <template #default="{ row }">
                <el-button-group size="small">
                  <el-button
                    v-if="row.status === 'PENDING' && userStore.isSampleManager"
                    @click.stop="handleEditWork(row)"
                  >
                    수정
                  </el-button>
                  <el-button
                    v-if="row.status === 'PENDING' && userStore.isSampleManager"
                    type="danger"
                    @click.stop="handleDeleteWork(row)"
                  >
                    삭제
                  </el-button>
                  <el-button
                    v-if="row.status === 'PENDING'"
                    type="primary"
                    @click.stop="handleWorkAction(row, 'start')"
                  >
                    시작
                  </el-button>
                  <el-button
                    v-if="row.status === 'IN_PROGRESS'"
                    type="success"
                    @click.stop="handleWorkAction(row, 'complete')"
                  >
                    완료
                  </el-button>
                </el-button-group>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <!-- 자재 내역 탭 (전체/레거시) -->
      <el-tab-pane label="자재 내역" name="materials">
        <el-card>
          <div class="tab-header">
            <span>자재 목록</span>
            <span>
              <el-button
                v-if="(sampleRequest?.materials || []).length"
                size="small"
                @click="downloadRequestForm"
              >
                의뢰서 다운로드
              </el-button>
              <el-button
                size="small"
                @click="downloadInputForm"
              >
                투입양식 다운로드
              </el-button>
              <el-button
                v-if="userStore.isSampleManager && canAddMaterial"
                type="primary"
                size="small"
                @click="openMaterialDialog"
              >
                자재 추가
              </el-button>
            </span>
          </div>
          <el-table :data="sampleRequest?.materials || []" stripe show-summary :summary-method="getMaterialSummary" row-key="id">
            <el-table-column type="expand">
              <template #default="{ row }">
                <div class="usage-detail">
                  <template v-if="row.usages && row.usages.length">
                    <span class="usage-title">부위별 소요량:</span>
                    <el-tag v-for="u in row.usages" :key="u.id" size="small" style="margin-right: 6px;">
                      {{ u.part }} {{ Number(u.usage_quantity) }}{{ u.unit }}
                    </el-tag>
                  </template>
                  <span v-else class="usage-empty">부위별 소요량 미입력 — 수정에서 입력하면 견적 산출에 활용됩니다</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="material_type_display" label="자재유형" width="100" />
            <el-table-column prop="material_name" label="자재명" min-width="150">
              <template #default="{ row }">
                {{ row.material_name }}
                <el-tag v-if="row.usages && row.usages.length" size="small" type="success" style="margin-left: 4px;">부위 {{ row.usages.length }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="회차" width="80" align="center">
              <template #default="{ row }">
                {{ row.round_number ? row.round_number + '차' : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="specification" label="규격" width="120" />
            <el-table-column prop="color" label="색상" width="80" />
            <el-table-column prop="purchase_type_display" label="구매유형" width="100" />
            <el-table-column prop="supplier_display" label="공급처" width="120" />
            <el-table-column prop="quantity" label="수량" width="80" align="right" />
            <el-table-column prop="unit" label="단위" width="60" />
            <el-table-column label="단가" width="100" align="right">
              <template #default="{ row }">
                {{ formatCurrency(row.unit_price) }}
              </template>
            </el-table-column>
            <el-table-column prop="total_price" label="합계" width="120" align="right">
              <template #default="{ row }">
                {{ formatCurrency(row.total_price) }}
              </template>
            </el-table-column>
            <el-table-column v-if="userStore.isSampleManager && canAddMaterial" label="" width="70" align="center">
              <template #default="{ row }">
                <el-button size="small" text type="primary" @click="openMaterialDialog(row)">수정</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <!-- BT의뢰 탭 (C-3, 2026-07-17) -->
      <el-tab-pane label="BT의뢰" name="bt-requests">
        <el-card>
          <div class="tab-header">
            <span>BT(비커테스트) 의뢰 목록</span>
            <el-button type="primary" size="small" @click="openBtDialog()">
              BT의뢰 추가
            </el-button>
          </div>
          <el-table :data="btRequests" stripe>
            <el-table-column prop="request_date" label="의뢰일" width="110" />
            <el-table-column prop="swatch_name" label="스와치명" width="140" />
            <el-table-column prop="supplier_name" label="의뢰처" width="140" />
            <el-table-column prop="quantity_note" label="수량" width="140" />
            <el-table-column label="색상" min-width="160">
              <template #default="{ row }">
                {{ (row.colors || '').split('\n').filter(Boolean).join(', ') || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="due_date" label="납기일" width="110" />
            <el-table-column label="상태" width="90">
              <template #default="{ row }">
                <el-tag size="small" :type="row.status === 'DONE' ? 'success' : row.status === 'SENT' ? '' : 'info'">
                  {{ row.status_display }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="200" align="center">
              <template #default="{ row }">
                <el-button size="small" @click="downloadBtForm(row)">의뢰서</el-button>
                <el-button size="small" text type="primary" @click="openBtDialog(row)">수정</el-button>
                <el-button size="small" text type="danger" @click="deleteBtRequest(row)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-if="!btRequests.length" description="BT의뢰 내역 없음" :image-size="60" />
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 수정 다이얼로그 -->
    <SampleRequestFormDialog
      v-model="showEditDialog"
      :edit-data="editData"
      @saved="handleEditSaved"
    />

    <!-- 작업 다이얼로그 -->
    <SampleWorkFormDialog
      v-model="showWorkDialog"
      :sample-request-id="sampleRequestId"
      :edit-work="editingWork"
      @saved="fetchData"
    />

    <!-- 자재 다이얼로그 (추가/수정 + 부위별 소요량) -->
    <SampleMaterialFormDialog
      v-model="showMaterialDialog"
      :sample-request-id="sampleRequestId"
      :edit-data="editingMaterial"
      @saved="fetchData"
    />

    <!-- 통합 결재 상신 다이얼로그 -->
    <ApprovalSubmitDialog
      v-model="showSubmitDialog"
      document-type="sample_request"
      :document-id="sampleRequestId"
      document-api-path="sample-requests"
      @submitted="fetchData"
    />

    <!-- 승인 코멘트 다이얼로그 (레거시) -->
    <el-dialog v-model="showApprovalDialog" title="승인/반려 처리" width="400px">
      <el-form :model="approvalForm">
        <el-form-item label="코멘트">
          <el-input
            v-model="approvalForm.comments"
            type="textarea"
            :rows="3"
            placeholder="코멘트를 입력하세요 (선택)"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showApprovalDialog = false">취소</el-button>
        <el-button :type="approvalForm.is_approved ? 'success' : 'danger'" @click="submitApproval">
          {{ approvalForm.is_approved ? '승인' : '반려' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- BT의뢰 다이얼로그 (C-3, 2026-07-17) -->
    <el-dialog
      v-model="showBtDialog"
      :title="editingBt ? 'BT의뢰 수정' : 'BT의뢰 추가'"
      width="560px"
    >
      <el-form label-width="90px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="스와치명" required>
              <el-input v-model="btForm.swatch_name" placeholder="예: AK-1001" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="의뢰처">
              <el-input v-model="btForm.supplier_name" placeholder="원단 업체명" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="의뢰일">
              <el-date-picker
                v-model="btForm.request_date"
                type="date"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="납기일">
              <el-date-picker
                v-model="btForm.due_date"
                type="date"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="수량">
              <el-input v-model="btForm.quantity_note" placeholder="예: 칼라당 약 320YD" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="상태">
              <el-select v-model="btForm.status" style="width: 100%">
                <el-option label="작성중" value="DRAFT" />
                <el-option label="의뢰완료" value="SENT" />
                <el-option label="결과수령" value="DONE" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="색상 목록">
          <el-input
            v-model="btForm.colors"
            type="textarea"
            :rows="4"
            placeholder="한 줄에 한 색상씩 (의뢰서 1~10칸 출력)"
          />
        </el-form-item>
        <el-form-item label="담당">
          <el-input v-model="btForm.contact" placeholder="예: 김민지팀장/백지혜대리" />
        </el-form-item>
        <el-form-item label="메모">
          <el-input v-model="btForm.memo" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showBtDialog = false">취소</el-button>
        <el-button type="primary" @click="saveBtRequest">저장</el-button>
      </template>
    </el-dialog>

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/sample-request-detail'"
    />
  </div>
</template>

<script setup>
import {ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ArrowLeft, QuestionFilled, Edit } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import SampleRequestFormDialog from '@/components/sample/SampleRequestFormDialog.vue'
import SampleWorkFormDialog from '@/components/sample/SampleWorkFormDialog.vue'
import SampleMaterialFormDialog from '@/components/sample/SampleMaterialFormDialog.vue'
import ApprovalStepsTimeline from '@/components/approval/ApprovalStepsTimeline.vue'
import ApprovalStatusCard from '@/components/approval/ApprovalStatusCard.vue'
import ApprovalSubmitDialog from '@/components/approval/ApprovalSubmitDialog.vue'
import ApprovalActionButtons from '@/components/approval/ApprovalActionButtons.vue'
import SampleRoundTimeline from '@/components/sample/SampleRoundTimeline.vue'
import SampleRoundDetail from '@/components/sample/SampleRoundDetail.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

// ⚠ keep-alive 함정 픽스 (2026-08-03): 전역 route.params.id 를 computed 로
// 읽으면 다른 :id 라우트로 이동한 순간 살아있는 이 화면이 그 id 를 자기 것으로
// 착각해 엉뚱한 조회(404 폭탄, id 충돌 시 다른 문서 표시 위험).
// 이 화면 라우트일 때만 갱신하고, 재진입으로 id 가 바뀌면 재조회한다.
const sampleRequestId = ref(route.params.id)
watch(() => route.fullPath, () => {
  if (route.path.startsWith('/sample-requests/') && route.params.id
      && route.params.id !== sampleRequestId.value) {
    sampleRequestId.value = route.params.id
    fetchData()
    fetchBtRequests()
  }
})
const loading = ref(false)
const sampleRequest = ref(null)
const activeTab = ref('approval-flow')
const showSubmitDialog = ref(false)
const helpVisible = ref(false)
const showEditDialog = ref(false)
const showWorkDialog = ref(false)
const showMaterialDialog = ref(false)
const showApprovalDialog = ref(false)
const selectedRound = ref(null)
const editingWork = ref(null)

const approvalForm = reactive({
  type: '',
  is_approved: true,
  comments: ''
})

// 통합 결재 요청 데이터
const unifiedApprovalRequest = computed(() => {
  return sampleRequest.value?.approval_request || null
})

// 현재 차례 결재자 이름 (배너 표시용)
const currentApproverName = computed(() => {
  const ar = unifiedApprovalRequest.value
  if (!ar) return ''
  const steps = ar.approval_steps || ar.steps || []
  const current = steps
    .filter((s) => !s.step_type || s.step_type === 'APPROVAL')
    .find((s) => s.status === 'PENDING')
  return current?.approver_name || ''
})

// 다단계 승인 데이터 유무
const hasApprovalSteps = computed(() => {
  return (sampleRequest.value?.approval_steps || []).length > 0
})

// 결재 상신 가능 여부 (REQUESTED 상태 + 통합결재 없음 + 레거시 없음)
const canSubmitApproval = computed(() => {
  if (!sampleRequest.value) return false
  if (!userStore.isSampleManager) return false
  return sampleRequest.value.status === 'REQUESTED' && !unifiedApprovalRequest.value && !hasApprovalSteps.value
})

// 회차 데이터 유무
const hasRounds = computed(() => {
  return (sampleRequest.value?.rounds || []).length > 0
})

// 레거시 승인 액션 표시 여부
const showLegacyApprovalActions = computed(() => {
  if (!sampleRequest.value) return false
  const status = sampleRequest.value.status
  // 다단계 승인이 있으면 승인 Flow 탭에서 처리
  if (hasApprovalSteps.value && ['PENDING_APPROVAL'].includes(status)) return false
  return ['REQUESTED', 'DEPT_APPROVED', 'FINAL_APPROVED', 'IN_PROGRESS'].includes(status)
})

// ── 이중 승인 정리 (2026-06-13): 주 경로=통합결재 상신, 직접승인은 fallback ──
// 결재 그룹 노출 (REQUESTED/DEPT_APPROVED — 결재 처리 단계)
const showApprovalGroup = computed(() => {
  const st = sampleRequest.value?.status
  return ['REQUESTED', 'DEPT_APPROVED'].includes(st)
})
// 레거시 직접 부서장승인 — 통합결재 상신이 불가할 때(결재선 미설정 등)만 fallback
const showLegacyDirectApproval = computed(() => {
  const st = sampleRequest.value?.status
  return st === 'REQUESTED' && userStore.isSampleApprover &&
    !hasApprovalSteps.value && !unifiedApprovalRequest.value && !canSubmitApproval.value
})
// 작업 그룹 노출 (FINAL_APPROVED/IN_PROGRESS — 작업 진행 단계)
const showWorkGroup = computed(() => {
  const st = sampleRequest.value?.status
  return ['FINAL_APPROVED', 'IN_PROGRESS'].includes(st)
})

// 레거시 작업 추가 가능 여부
const canAddWorkLegacy = computed(() => {
  return sampleRequest.value?.status === 'IN_PROGRESS' && !hasRounds.value
})

// 수정 가능 여부 (초기 상태에서만)
const canEdit = computed(() => {
  if (!sampleRequest.value) return false
  if (!userStore.isSampleManager) return false
  return ['REQUESTED', 'PENDING_APPROVAL'].includes(sampleRequest.value.status)
})

// 샘플 → 견적: 자재가 모인 승인 이후 단계에서 노출 (디자인 연결 필수)
const canCreateQuotation = computed(() => {
  const sr = sampleRequest.value
  if (!sr || !sr.design) return false
  return ['FINAL_APPROVED', 'APPROVED', 'IN_PROGRESS', 'COMPLETED'].includes(sr.status)
})
const goCreateQuotation = () => {
  const sr = sampleRequest.value
  router.push({
    path: '/quotations',
    query: {
      design: sr.design, brand: sr.brand || '',
      sample: sr.id, sampleNo: sr.request_number || '',
    },
  })
}

// 수정용 데이터
const editData = computed(() => {
  if (!sampleRequest.value) return null
  return {
    id: sampleRequest.value.id,
    brand: sampleRequest.value.brand,
    design: sampleRequest.value.design,
    sample_type: sampleRequest.value.sample_type,
    quantity: sampleRequest.value.quantity,
    request_date: sampleRequest.value.request_date,
    due_date: sampleRequest.value.due_date,
    colors: sampleRequest.value.colors,
    sizes: sampleRequest.value.sizes,
    designer: sampleRequest.value.designer,
    estimated_cost: sampleRequest.value.estimated_cost,
    memo: sampleRequest.value.memo,
  }
})

// 자재 추가 가능 여부 (승인 완료 이후 상태에서만)
const canAddMaterial = computed(() => {
  if (!sampleRequest.value) return false
  const approvedStatuses = ['FINAL_APPROVED', 'APPROVED', 'IN_PROGRESS']
  return approvedStatuses.includes(sampleRequest.value.status)
})

const getStatusType = (status) => {
  const types = {
    'REQUESTED': 'info',
    'PENDING_APPROVAL': 'warning',
    'DEPT_APPROVED': 'warning',
    'APPROVED': 'success',
    'FINAL_APPROVED': 'success',
    'IN_PROGRESS': '',
    'COMPLETED': 'success',
    'REJECTED': 'danger'
  }
  return types[status] || 'info'
}

const getWorkStatusType = (status) => {
  const types = {
    'PENDING': 'info',
    'IN_PROGRESS': '',
    'COMPLETED': 'success',
    'HOLD': 'warning'
  }
  return types[status] || 'info'
}

const formatCurrency = (amount) => {
  if (!amount) return '-'
  return Number(amount).toLocaleString() + '원'
}

const fetchData = async () => {
  loading.value = true
  try {
    const response = await axiosInstance.get(`sample-requests/${sampleRequestId.value}/`)
    sampleRequest.value = response.data

    // 선택된 회차 갱신
    if (selectedRound.value) {
      const updatedRound = (response.data.rounds || []).find(r => r.id === selectedRound.value.id)
      if (updatedRound) {
        // 회차 상세를 별도로 가져옴 (works, materials 포함)
        try {
          const roundResp = await axiosInstance.get(`sample-rounds/${updatedRound.id}/`)
          selectedRound.value = roundResp.data
        } catch {
          selectedRound.value = updatedRound
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch sample request:', error)
    ElMessage.error('샘플 요청 정보를 불러오지 못했습니다.')
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.push('/sample-requests')
}

const showHelp = () => {
  helpVisible.value = true
}

const handleApprove = (type, isApproved) => {
  approvalForm.type = type
  approvalForm.is_approved = isApproved
  approvalForm.comments = ''
  showApprovalDialog.value = true
}

const submitApproval = async () => {
  try {
    const endpoint = approvalForm.type === 'dept' ? 'approve_dept' : 'approve_final'
    await axiosInstance.post(`sample-requests/${sampleRequestId.value}/${endpoint}/`, {
      is_approved: approvalForm.is_approved,
      comments: approvalForm.comments
    })
    ElMessage.success(approvalForm.is_approved ? '승인되었습니다.' : '반려되었습니다.')
    showApprovalDialog.value = false
    fetchData()
  } catch (error) {
    console.error('Approval failed:', error)
    ElMessage.error(error.response?.data?.error || '처리 중 오류가 발생했습니다.')
  }
}

const handleStartWork = async () => {
  try {
    await ElMessageBox.confirm('작업을 시작하시겠습니까?', '작업 시작')
    await axiosInstance.post(`sample-requests/${sampleRequestId.value}/start_work/`)
    ElMessage.success('작업이 시작되었습니다.')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Start work failed:', error)
      ElMessage.error(error.response?.data?.error || '처리 중 오류가 발생했습니다.')
    }
  }
}

const handleComplete = async () => {
  try {
    await ElMessageBox.confirm('완료 처리하시겠습니까?', '완료 처리')
    await axiosInstance.post(`sample-requests/${sampleRequestId.value}/complete/`)
    ElMessage.success('완료 처리되었습니다.')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Complete failed:', error)
      ElMessage.error(error.response?.data?.error || '처리 중 오류가 발생했습니다.')
    }
  }
}

const handleWorkAction = async (work, action) => {
  try {
    await axiosInstance.post(`sample-works/${work.id}/${action}/`)
    ElMessage.success(action === 'start' ? '작업이 시작되었습니다.' : '작업이 완료되었습니다.')
    fetchData()
  } catch (error) {
    console.error('Work action failed:', error)
    ElMessage.error(error.response?.data?.error || '처리 중 오류가 발생했습니다.')
  }
}

const handleSelectRound = async (round) => {
  try {
    const response = await axiosInstance.get(`sample-rounds/${round.id}/`)
    selectedRound.value = response.data
  } catch (error) {
    console.error('Failed to fetch round detail:', error)
    selectedRound.value = round
  }
}

const openEditDialog = () => {
  showEditDialog.value = true
}

const handleEditSaved = () => {
  showEditDialog.value = false
  fetchData()
}

const openWorkDialog = () => {
  editingWork.value = null
  showWorkDialog.value = true
}

const handleEditWork = (work) => {
  editingWork.value = work
  showWorkDialog.value = true
}

const handleDeleteWork = async (work) => {
  try {
    await ElMessageBox.confirm('이 작업을 삭제하시겠습니까?', '작업 삭제', {
      type: 'warning'
    })
    await axiosInstance.delete(`sample-works/${work.id}/`)
    ElMessage.success('작업이 삭제되었습니다.')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Delete work failed:', error)
      ElMessage.error(error.response?.data?.detail || '삭제 중 오류가 발생했습니다.')
    }
  }
}

const editingMaterial = ref(null)

// ── BT의뢰 (C-3, 2026-07-17) ──
const btRequests = ref([])
const showBtDialog = ref(false)
const editingBt = ref(null)
const btForm = reactive({
  swatch_name: '', supplier_name: '', request_date: '', due_date: null,
  quantity_note: '', colors: '', contact: '', status: 'DRAFT', memo: '',
})

const fetchBtRequests = async () => {
  try {
    const res = await axiosInstance.get('sample-test-requests/', {
      params: { sample_request: route.params.id },
    })
    btRequests.value = res.data.results || res.data
  } catch (e) {
    console.error('Failed to fetch BT requests:', e)
  }
}

const openBtDialog = (row = null) => {
  editingBt.value = row
  btForm.swatch_name = row?.swatch_name || ''
  btForm.supplier_name = row?.supplier_name || ''
  btForm.request_date = row?.request_date || new Date().toISOString().slice(0, 10)
  btForm.due_date = row?.due_date || null
  btForm.quantity_note = row?.quantity_note || ''
  btForm.colors = row?.colors || (sampleRequest.value?.colors || '').split(',').map(c => c.trim()).filter(Boolean).join('\n')
  btForm.contact = row?.contact || ''
  btForm.status = row?.status || 'DRAFT'
  btForm.memo = row?.memo || ''
  showBtDialog.value = true
}

const saveBtRequest = async () => {
  if (!btForm.swatch_name.trim()) {
    ElMessage.warning('스와치명을 입력하세요.')
    return
  }
  try {
    const payload = { ...btForm, sample_request: route.params.id }
    if (editingBt.value) {
      await axiosInstance.patch(`sample-test-requests/${editingBt.value.id}/`, payload)
    } else {
      await axiosInstance.post('sample-test-requests/', payload)
    }
    ElMessage.success('BT의뢰가 저장되었습니다.')
    showBtDialog.value = false
    fetchBtRequests()
  } catch (e) {
    ElMessage.error('저장 실패: ' + (e.response?.data?.detail || e.message))
  }
}

const deleteBtRequest = async (row) => {
  try {
    await ElMessageBox.confirm('이 BT의뢰를 삭제하시겠습니까?', '삭제', { type: 'warning' })
    await axiosInstance.delete(`sample-test-requests/${row.id}/`)
    ElMessage.success('삭제되었습니다.')
    fetchBtRequests()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error('삭제 실패')
  }
}

const downloadBtForm = async (row) => {
  try {
    const res = await axiosInstance.get(`sample-test-requests/${row.id}/form/`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `BT의뢰서_${row.swatch_name}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('의뢰서 생성 실패')
  }
}

// ── 투입양식/자재설명서 (C-5, 2026-07-17) ──
const downloadInputForm = async () => {
  try {
    const res = await axiosInstance.get('/sample-materials/input-form/', {
      params: { sample_request: route.params.id },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `투입양식_${sampleRequest.value?.request_number || route.params.id}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('투입양식 생성 실패')
  }
}
// 샘플자재의뢰서 서식 출력 (C-4, 2026-07-17) — 업체별 시트 xlsx
const downloadRequestForm = async () => {
  try {
    const res = await axiosInstance.get('/sample-materials/request-form/', {
      params: { sample_request: route.params.id },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `샘플자재의뢰서_${sampleRequest.value?.request_number || route.params.id}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('의뢰서 생성 실패')
  }
}

const openMaterialDialog = (row = null) => {
  // row 가 자재 객체면 수정, 아니면(이벤트/없음) 신규
  editingMaterial.value = row && row.id ? {
    id: row.id,
    material_type: row.material_type, purchase_type: row.purchase_type,
    material_name: row.material_name, specification: row.specification, color: row.color,
    supplier: row.supplier, quantity: Number(row.quantity), unit: row.unit,
    unit_price: Number(row.unit_price), purchase_location: row.purchase_location,
    memo: row.memo, usages: row.usages || [],
  } : null
  showMaterialDialog.value = true
}

const getMaterialSummary = ({ columns, data }) => {
  const sums = []
  columns.forEach((column, index) => {
    if (index === 0) {
      sums[index] = '합계'
      return
    }
    if (column.property === 'total_price') {
      const total = data.reduce((sum, item) => sum + Number(item.total_price || 0), 0)
      sums[index] = formatCurrency(total)
    } else {
      sums[index] = ''
    }
  })
  return sums
}

onMounted(() => {
  fetchData()
  fetchBtRequests()
})
</script>

<style scoped>
.sample-request-detail {
  padding: 20px;
}

/* 자재 부위별 소요량 expand */
.usage-detail { padding: 6px 20px; }
.usage-title { font-size: 12px; color: #606266; margin-right: 8px; }
.usage-empty { font-size: 12px; color: #c0c4cc; }

/* 승인/작업 액션 그룹 구분 */
.approval-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.approval-label { font-weight: 600; color: #606266; }
.approval-label.work-label { margin-left: 12px; padding-left: 12px; border-left: 1px solid #ebeef5; }
.approval-hint { font-size: 12px; color: #e6a23c; margin-left: 4px; }

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h2 {
  margin: 0;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  text-align: center;
  padding: 10px;
}

.summary-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.text-danger {
  color: #f56c6c;
}

.approval-card {
  margin-bottom: 12px;
}

/* ===== 정보 패널 (밀도 우선) ===== */
.info-panel {
  margin-bottom: 12px;
}
.info-panel :deep(.el-card__body) {
  padding: 12px 16px;
}
.panel-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.panel-image {
  flex-shrink: 0;
  border: 1px solid #f0f2f5;
  border-radius: 6px;
  overflow: hidden;
}
.panel-grid {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px 24px;
}
.info-item .v.strong {
  font-weight: 700;
}

/* 내 차례 결재 배너 */
.my-turn-banner {
  border-left: 4px solid #6f42c1;
}
.my-turn-banner :deep(.el-card__body) {
  padding: 10px 16px;
}
.banner-sub {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}
.banner-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.banner-text {
  font-size: 14px;
  color: #303133;
}

/* 요청 정보 카드 */
.request-info-card .info-head {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 20px;
}
.info-item {
  display: flex;
  gap: 8px;
  font-size: 13px;
  padding: 3px 0;
}
.info-item .k,
.info-memo .k {
  color: #909399;
  flex-shrink: 0;
  width: 80px;
}
.info-item .v,
.info-memo .v {
  color: #303133;
  word-break: break-word;
}
.info-memo {
  display: flex;
  gap: 8px;
  font-size: 13px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f5f7fa;
}

@media (max-width: 768px) {
  .info-grid,
  .panel-grid {
    grid-template-columns: 1fr;
  }
  .panel-row {
    flex-direction: column;
  }
}

.approval-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.approval-label {
  font-weight: 600;
  color: #606266;
}

.detail-tabs {
  margin-top: 20px;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.tab-header span {
  font-size: 16px;
  font-weight: 600;
}

.design-image-card {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.design-image-card :deep(.el-card__body) {
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: #c0c4cc;
  font-size: 12px;
}
</style>
